import React, { useState, useEffect } from "react";
import { useAuth } from "../context/Auth/AuthState";
import useTheme from "../components/useTheme/useTheme";
import BreadCrumb from "../components/Breadcrumb/Breadcrumb";
import FooterV2 from "../components/FooterV2/FooterV2";
import { toast } from "react-toastify";
import ToastMsg from "../components/ToastMsg/ToastMsg";
import http from "../helpers/http";
import config from "../config/config";
import Joi from "joi";
import "./feedback.scss";

const BusinessFeedback = () => {
  const auth = useAuth();
  const { theme } = useTheme();

  const [formData, setFormData] = useState({
    feedback_topic: "",
    feedback_message: "",
  });

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const base_url = config.api_url;

  const topicOptions = [
    { value: "", label: "--None--" },
    { value: "Feature Request", label: "Feature Request" },
    { value: "Bug / Technical Issue", label: "Bug / Technical Issue" },
    { value: "UI / UX Improvement", label: "UI / UX Improvement" },
    { value: "Business Profile / Visibility Issue", label: "Business Profile / Visibility Issue" },
    { value: "Subscription / Pricing Feedback", label: "Subscription / Pricing Feedback" },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (formError[name]) {
      setFormError((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const schema = Joi.object({
      feedback_topic: Joi.string()
        .valid(
          "Feature Request",
          "Bug / Technical Issue",
          "UI / UX Improvement",
          "Business Profile / Visibility Issue",
          "Subscription / Pricing Feedback"
        )
        .required()
        .messages({
          "any.only": "Please select a valid topic",
          "string.empty": "Please select a topic",
        }),
      feedback_message: Joi.string()
        .min(10)
        .required()
        .messages({
          "string.empty": "Feedback message is required",
          "string.min": "Feedback message must be at least 10 characters",
        }),
    });

    const { error } = schema.validate(formData, { abortEarly: false });

    if (error) {
      const errors = {};
      error.details.forEach((detail) => {
        errors[detail.path[0]] = detail.message;
      });
      return errors;
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    setFormError(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);

    const payload = {
      business_name: auth?.user?.business_name || "",
      business_email: auth?.user?.email || "",
      contact_person_name: auth?.user?.contact_person || "",
      contact_person_phone: auth?.user?.phone ? `${auth?.user?.country_code || ""} ${auth?.user?.phone}`.trim() : "",
      feedback_topic: formData.feedback_topic,
      feedback_message: formData.feedback_message,
    };

    try {
      const response = await http.post(`${base_url}/business-feedback/submit`, payload);

      if (response?.data) {
        setSubmitSuccess(true);
        setFormData({
          feedback_topic: "",
          feedback_message: "",
        });
        toast(
          <ToastMsg message="Feedback submitted successfully!" />,
          config.success_toast_config
        );
      }
    } catch (err) {
      toast(
        <ToastMsg message="Failed to submit feedback. Please try again." danger={true} />,
        config.error_toast_config
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className={`feedback_main ${theme === "light" ? "light_theme" : ""}`}>
        <section className="feedback_sec1">
          <div className="my_container">
            <div
              className={`Breadcrumb_container ${
                theme === "light" ? "breadcrumb_container_light_mode" : ""
              }`}
            >
              <BreadCrumb
                link="/business-feedback"
                text="Business Feedback"
                title="Share Your Feedback"
                linkDisabled
              />
            </div>
          </div>
        </section>

        <section className="feedback_sec2">
          <div className="my_container">
            <div className="feedback_form_wrapper">
              <h2 className="form_title">We'd love to hear from you</h2>
              <p className="form_subtitle">
                Your feedback helps us improve Archinza for businesses.
              </p>

              {submitSuccess ? (
                <div className="success_message">
                  <h3>Thank you for your feedback!</h3>
                  <p>We appreciate you taking the time to share your thoughts with us.</p>
                  <button
                    className="submit_another_btn"
                    onClick={() => setSubmitSuccess(false)}
                  >
                    Submit Another Feedback
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="feedback_form">
                  {/* Read-only business info fields */}
                  <div className="feedback_form_row readonly_section">
                    <div className="form_group">
                      <label className="form_label">Business Name</label>
                      <input
                        type="text"
                        className="form_input readonly"
                        value={auth?.user?.business_name || ""}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="form_group">
                      <label className="form_label">Business Email</label>
                      <input
                        type="email"
                        className="form_input readonly"
                        value={auth?.user?.email || ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  <div className="feedback_form_row readonly_section">
                    <div className="form_group">
                      <label className="form_label">Contact Person Name</label>
                      <input
                        type="text"
                        className="form_input readonly"
                        value={auth?.user?.contact_person || ""}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="form_group">
                      <label className="form_label">Contact Person Phone</label>
                      <input
                        type="text"
                        className="form_input readonly"
                        value={auth?.user?.phone ? `${auth?.user?.country_code || ""} ${auth?.user?.phone}` : ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  {/* Editable fields */}
                  <div className="feedback_form_row">
                    <div className="form_group full_width">
                      <label className="form_label">
                        Pick a Topic <span className="required">*</span>
                      </label>
                      <select
                        name="feedback_topic"
                        className={`form_select ${formError.feedback_topic ? "error" : ""}`}
                        value={formData.feedback_topic}
                        onChange={handleChange}
                      >
                        {topicOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {formError.feedback_topic && (
                        <span className="error_text">{formError.feedback_topic}</span>
                      )}
                    </div>
                  </div>

                  <div className="feedback_form_row">
                    <div className="form_group full_width">
                      <label className="form_label">
                        Description <span className="required">*</span>
                      </label>
                      <textarea
                        name="feedback_message"
                        className={`form_textarea ${formError.feedback_message ? "error" : ""}`}
                        value={formData.feedback_message}
                        onChange={handleChange}
                        placeholder="Tell us what's on your mind..."
                        rows={6}
                      />
                      {formError.feedback_message && (
                        <span className="error_text">{formError.feedback_message}</span>
                      )}
                    </div>
                  </div>

                  <div className="form_actions">
                    <button
                      type="submit"
                      className="submit_btn"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit Feedback"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      {theme === "dark" ? (
        <FooterV2 bgColor="#000000" />
      ) : (
        <FooterV2 lightTheme bgColor="#ffffff" />
      )}
    </>
  );
};

export default BusinessFeedback;
